import { defineStore, type Pinia, type StoreGeneric } from "pinia";

type UseStore = ((pinia?: Pinia | null, hot?: StoreGeneric) => StoreGeneric) & { $id: string; _pinia?: Pinia };

/**
 * The Pinia instance of the request the caller runs in, or undefined where Pinia finds it by
 * itself (inside a component, plugin or `runWithContext()`, it injects the app's own instance).
 *
 * Elsewhere, e.g. after an `await` in an action or a provider, Pinia falls back to
 * the process-wide active Pinia. On the server that is the Pinia of whichever request set it last,
 * so a request would read and write another user's stores, access token included (CE-3138). The
 * Nuxt app of the current request is still known there through Nuxt's async context
 * (`experimental.asyncContext`); store actions run in it (plugins/pinia-request-context.server.ts).
 */
function requestPinia(): Pinia | undefined {
  if (hasInjectionContext())
    return undefined;

  const pinia = tryUseNuxtApp()?.$pinia;
  if (!pinia && import.meta.server)
    throw new Error("[requestStore] A store was used on the server outside of a request context. Call it from a component, a plugin, a middleware or a store action.");
  return pinia;
}

/**
 * Drop-in replacement for Pinia's `defineStore` that every store has to use: the returned
 * composable resolves the store from the current request's Pinia instead of the global one.
 */
export const defineRequestStore = ((...args: Parameters<typeof defineStore>) => {
  const useStore = (defineStore as (...a: typeof args) => UseStore)(...args);
  const useRequestStore = (pinia?: Pinia | null, hot?: StoreGeneric) => useStore(pinia ?? requestPinia(), hot);
  useRequestStore.$id = useStore.$id;
  // Read by acceptHMRUpdate() in development.
  Object.defineProperty(useRequestStore, "_pinia", { get: () => useStore._pinia });
  return useRequestStore;
}) as typeof defineStore;
